import { useToast } from "@lib/hooks/useToast";
import { MessageSquare, Send } from "lucide-react";
import { useState } from "react";
import { useTicketComments } from "../../hooks/useTicketComments";
import { useCreateTicketComment } from "../../hooks/useTicketMutations";
import type { TicketComment } from "../../types/ticket.types";
import { formatRelativeDate, getInitials } from "../../utils";

interface TicketCommentsProps {
  ticketId: number | string;
  comments?: readonly TicketComment[];
}

export function TicketComments({ ticketId, comments: propComments }: TicketCommentsProps) {
  const [content, setContent] = useState("");
  const createComment = useCreateTicketComment();
  const toast = useToast();
  const { data: fetchedComments } = useTicketComments(ticketId);

  // Prefer the dedicated comments endpoint; fall back to the embedded prop.
  const comments = fetchedComments ?? propComments ?? [];

  const handleSubmit = () => {
    if (!content.trim()) return;

    createComment.mutate(
      { ticket: ticketId, content: content.trim() },
      {
        onSuccess: () => {
          setContent("");
        },
        onError: () => {
          toast.error("Erreur lors de l'ajout du commentaire");
        },
      },
    );
  };

  return (
    <div className="card bg-base-200 border border-base-300/70 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200">
      <div className="card-body space-y-4">
        <h3 className="card-title text-sm font-semibold gap-2">
          <MessageSquare className="w-4 h-4" />
          Commentaires ({comments.length})
        </h3>

        {/* Existing comments */}
        {comments.length > 0 && (
          <div className="space-y-3">
            {comments.map((comment) => (
              <CommentCard key={comment.id} comment={comment} />
            ))}
          </div>
        )}

        {comments.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-6 text-base-content/40">
            <MessageSquare className="h-8 w-8 opacity-40" />
            <p className="text-xs">Aucun commentaire — soyez le premier à commenter</p>
          </div>
        )}

        {/* Comment input */}
        <div className="flex gap-2">
          <textarea
            className="textarea textarea-bordered flex-1 bg-base-200 border-base-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 min-h-[80px]"
            placeholder="Ajouter un commentaire..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            className="btn btn-primary btn-sm gap-1.5 active:scale-95 transition-transform"
            onClick={handleSubmit}
            disabled={!content.trim() || createComment.isPending}
          >
            {createComment.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Envoyer
          </button>
        </div>

        <p className="text-xs text-base-content/40">Ctrl+Entrée pour envoyer</p>
      </div>
    </div>
  );
}

function CommentCard({ comment }: { comment: TicketComment }) {
  const authorId = comment.authorId ?? comment.creatorId;
  const authorName = comment.creatorName?.trim() || `Utilisateur #${authorId}`;
  const initials = getInitials(comment.creatorName);

  return (
    <div className="flex gap-3 p-3 rounded-lg bg-base-200/50 border border-base-200 hover:bg-base-200/70 transition-colors">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/60 to-primary/30 flex items-center justify-center text-xs font-semibold text-white shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">{authorName}</span>
          <span className="text-xs text-base-content/40">
            <time dateTime={comment.createdAt}>{formatRelativeDate(comment.createdAt)}</time>
          </span>
        </div>
        <p className="text-sm text-base-content/80 whitespace-pre-wrap">{comment.content}</p>
      </div>
    </div>
  );
}
